/**
 * WebSocket Service - real-time communication with the backend.
 */

import config, { buildWsUrl } from '../config';

export const ConnectionState = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error',
} as const;

export const MessageTypes = {
  MUTE_USER: 'MUTE_USER',
  UNMUTE_USER: 'UNMUTE_USER',
  SPEAKING_UPDATE: 'SPEAKING_UPDATE',
  INTERRUPTION_DETECTED: 'INTERRUPTION_DETECTED',
  TRANSCRIPT: 'TRANSCRIPT',
  MUTE_RECOMMENDATION: 'MUTE_RECOMMENDATION',
  PARTICIPANT_LIST: 'PARTICIPANT_LIST',
  OUTPUTS_UPDATE: 'OUTPUTS_UPDATE',
  /** Full meeting state (agenda + participants + lifecycle) — same payload as GET /api/zoom/state */
  MEETING_STATE_UPDATE: 'MEETING_STATE_UPDATE',
} as const;

type ConnectionStateType = (typeof ConnectionState)[keyof typeof ConnectionState];
type WebSocketEventType = 'message' | 'stateChange' | 'error';

interface WebSocketManagerOptions {
  onMessage?: (data: unknown) => void;
  onStateChange?: (state: ConnectionStateType) => void;
  onError?: (error: Error) => void;
  maxReconnectAttempts?: number;
  reconnectInterval?: number;
  heartbeatIntervalMs?: number;
}

interface WebSocketManager {
  connect: (endpoint: string) => void;
  disconnect: () => void;
  send: (data: object) => boolean;
  on: (event: WebSocketEventType, callback: (data: unknown) => void) => void;
  off: (event: WebSocketEventType, callback: (data: unknown) => void) => void;
  getState: () => ConnectionStateType;
  isConnected: () => boolean;
}

export const createWebSocketManager = (options: WebSocketManagerOptions = {}): WebSocketManager => {
  let ws: WebSocket | null = null;
  let reconnectAttempts = 0;
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  let state: ConnectionStateType = ConnectionState.DISCONNECTED;

  const listeners: { message: ((d: unknown) => void)[]; stateChange: ((s: ConnectionStateType) => void)[]; error: ((e: Error) => void)[] } = {
    message: [],
    stateChange: [],
    error: [],
  };

  const {
    onMessage,
    onStateChange,
    onError,
    maxReconnectAttempts = config.ws.maxReconnectAttempts,
    reconnectInterval = config.ws.reconnectInterval,
    heartbeatIntervalMs = config.ws.heartbeatInterval,
  } = options;

  if (onMessage) listeners.message.push(onMessage);
  if (onStateChange) listeners.stateChange.push(onStateChange);
  if (onError) listeners.error.push(onError);

  const emit = (event: WebSocketEventType, data: unknown): void => {
    (listeners[event] as ((d: unknown) => void)[]).forEach((cb) => {
      try {
        cb(data);
      } catch (err) {
        console.error(`[WebSocket] Error in ${event} listener:`, err);
      }
    });
  };

  const setState = (newState: ConnectionStateType): void => {
    if (state !== newState) {
      state = newState;
      emit('stateChange', newState);
    }
  };

  const startHeartbeat = (): void => {
    if (heartbeatInterval) clearInterval(heartbeatInterval);
    heartbeatInterval = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'PING', timestamp: Date.now() }));
      }
    }, heartbeatIntervalMs);
  };

  const stopHeartbeat = (): void => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
  };

  const scheduleReconnect = (endpoint: string): void => {
    if (reconnectAttempts >= maxReconnectAttempts) {
      setState(ConnectionState.ERROR);
      emit('error', new Error('Max reconnection attempts reached'));
      return;
    }
    setState(ConnectionState.RECONNECTING);
    reconnectAttempts++;
    // Exponential backoff: base interval * 2^(attempt-1), capped at 30s
    const backoffMs = Math.min(reconnectInterval * Math.pow(2, reconnectAttempts - 1), 30000);
    reconnectTimeout = setTimeout(() => connect(endpoint), backoffMs);
  };

  const connect = (endpoint: string): void => {
    disconnect();
    setState(ConnectionState.CONNECTING);
    const url = buildWsUrl(endpoint);
    try {
      ws = new WebSocket(url);
      ws.onopen = () => {
        reconnectAttempts = 0;
        setState(ConnectionState.CONNECTED);
        startHeartbeat();
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data as string);
          emit('message', data);
        } catch (err) {
          console.error('[WebSocket] Failed to parse message:', err);
        }
      };
      ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        emit('error', new Error('WebSocket error'));
      };
      ws.onclose = (event) => {
        stopHeartbeat();
        if (event.wasClean) {
          setState(ConnectionState.DISCONNECTED);
        } else {
          scheduleReconnect(endpoint);
        }
      };
    } catch (error) {
      setState(ConnectionState.ERROR);
      emit('error', error instanceof Error ? error : new Error(String(error)));
    }
  };

  const disconnect = (): void => {
    stopHeartbeat();
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }
    if (ws) {
      ws.onclose = null;
      ws.close();
      ws = null;
    }
    setState(ConnectionState.DISCONNECTED);
  };

  const send = (data: object): boolean => {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(data));
      return true;
    }
    return false;
  };

  const on = (event: WebSocketEventType, callback: (data: unknown) => void): void => {
    if (listeners[event]) listeners[event].push(callback as never);
  };

  const off = (event: WebSocketEventType, callback: (data: unknown) => void): void => {
    if (listeners[event]) {
      (listeners[event] as ((d: unknown) => void)[]) = (listeners[event] as ((d: unknown) => void)[]).filter(
        (cb) => cb !== callback
      );
    }
  };

  const getState = (): ConnectionStateType => state;
  const isConnected = (): boolean => state === ConnectionState.CONNECTED;

  return { connect, disconnect, send, on, off, getState, isConnected };
};

export const createModerationWebSocket = (
  meetingUUID: string,
  hostParticipantUUID: string,
  options: WebSocketManagerOptions = {}
): WebSocketManager => {
  const endpoint = config.wsEndpoints.moderation(meetingUUID, hostParticipantUUID);
  const manager = createWebSocketManager(options);
  manager.connect(endpoint);
  return manager;
};

export const createDashboardWebSocket = (options: WebSocketManagerOptions = {}): WebSocketManager => {
  const endpoint = config.wsEndpoints.dashboard(config.dashboardToken);
  const manager = createWebSocketManager(options);
  manager.connect(endpoint);
  return manager;
};

/**
 * Create a WebSocket for receiving per-user live meeting insights.
 * Connects to /ws/moderation?meetingId=X&access_token=Y — the same authenticated
 * path the web dashboard uses (zoomModerationServer.ts handleDashboardConnection).
 */
export const createInsightsWebSocket = (
  meetingId: string,
  accessToken: string,
  options: WebSocketManagerOptions = {},
): WebSocketManager => {
  const endpoint = `/ws/moderation?meetingId=${encodeURIComponent(meetingId)}&access_token=${encodeURIComponent(accessToken)}`;
  const manager = createWebSocketManager(options);
  manager.connect(endpoint);
  return manager;
};

export default {
  createWebSocketManager,
  createModerationWebSocket,
  createDashboardWebSocket,
  createInsightsWebSocket,
  ConnectionState,
  MessageTypes,
};
